import { $$, breakpoint, debounce } from 'dlr-utils/helpers';

/**
 * IMPORTANT: THIS WILL ALWAYS BE AN ARRAY
 * Always make it possible for multiple instances
 * of your module to work on the pages.
 */
const SELECTOR = '.dlr-homepage-hero';
const INSTANCES = $$(SELECTOR);

/* If you want to target just one, just uncomment the line below */
const ELEMENT = INSTANCES[0];

/**
 * 1. Mounting
 *
 * Put module-specific variables and functions here.
 * Actions in this scope will fire as soon as the module
 * is mounted (not yet attached to the page)
 *  e.g. setting of initial states (see below), data fetching, etc
 *
 * The default export functions are the only
 * ones used by the dynamic module importer (main.js).
 * upon page load. main.js will only fire these if
 * at least one element with the class ".dlr-homepage-hero" exists
 */
const state = {};
const mastheadDOM = ELEMENT.querySelector('.dlr-homepage-hero__primary-container');
const carouselDOM = ELEMENT.querySelector('.dlr-homepage-hero__secondary-container');
const mastheadTitle = mastheadDOM.querySelector('.dlr-homepage-hero__title');
const mastheadSub = mastheadDOM.querySelector('.dlr-homepage-hero__sub');
// Cache -- needs to be cleared later
let elemsWithBoundingRects = [];

export default {
  /**
   * 2. REGISTRATION
   *
   * Use `init` for actions that should fire as soon as
   * the module file has been made available to the page
   *  - e.g. data transformations, MessageBus Subscriptions
   */
    init(GlobalEvents, MessageBus) {
      const { onResize } = GlobalEvents;

      onResize(() => {
        this.startHero(GlobalEvents, MessageBus);
      });

    },

    startHero(GlobalEvents, MessageBus) {
      // on mobile, we don't do the special animation so we just return it
      if (breakpoint() === 'mobile') {
        // clear all styles
        mastheadDOM.removeAttribute('style');
        carouselDOM.removeAttribute('style');
        mastheadTitle.removeAttribute('style');
        mastheadSub.removeAttribute('style');

        return;
      }

      mastheadDOM.style.minWidth = '42%';
      this.animateHero(GlobalEvents, MessageBus);
    },

    animateHero(GlobalEvents, MessageBus) {
      const { onScroll } = GlobalEvents;
      let marginPerBreakpoint = 12; // margin between primary wrapper and carousel wrapper
      let allowanceOverlap = 50; // the distance we need to scroll before animating the carousel container
      let carouselSpeed = 1.8; // offset multiplier -- visibly changes the speed of the resizing
      let carouselMinWidth = 424; // min-width of the carousel wrapper -- mirroring the CSS specs
      let scrolledTop = 320; // static values based on the amount needed to scroll to trigger the animation (scales with height of the viewport)
      const carouselAnimationStart = 340; // static value indicating the start of the animation of the carousel's opacity
      const autoMarginSize = (window.innerWidth - 1440 <= 0 ? 0 : (window.innerWidth - 1440) / 2); // margin-auto compensation
      const isExtraLarge = window.innerWidth >= 1441; // start of the auto-margin, needs manual computation

      // re-evaluate values per breakpoint
      if (breakpoint() === 'tablet') {
        marginPerBreakpoint = 12;
        allowanceOverlap = 10;
      } else if (breakpoint() === 'desktop') {
        carouselMinWidth = 548;
        marginPerBreakpoint = 8;
        allowanceOverlap = 100;
        scrolledTop = 220;
      } else if (breakpoint() === 'desktop-large') {
        carouselSpeed = 2;
        allowanceOverlap = 200;
        marginPerBreakpoint = 4;
        // set carousel min-width on load
        carouselDOM.style.minWidth = `${800 + autoMarginSize}px`;
        scrolledTop = 50;

        // if extra large, compensate for the scaling margin
        if (isExtraLarge) {
          scrolledTop = autoMarginSize <= 120 ? -50 : (autoMarginSize / 1.5) * -1;
        }
      }

      const resizeAnimation = (offset = 0) => {

        // ⚠️  EXPERIMENT  ⚠️
        //  clientWidth and getBoundingClientRect both forces a reflow
        //  apparently, getBoundingClientRect still performs better than clientWidth

        const { bottom } = mastheadDOM.getBoundingClientRect();
        let mastheadDOMbottomSpace = window.innerHeight - bottom;

        // Hero transition
        const primaryContainerAnim = {
          shrink: () => {
            //  `trigger` reference mastheadDOM.getBoundingClientRect().bottom
            const trigger = {
              start: 0,
              stop: 235,
            };
            const perc = this.getPerc(mastheadDOMbottomSpace, trigger.start, trigger.stop) / 100;
            const shaveWidth = (window.innerWidth - window.innerWidth * 0.42) * perc;

            mastheadDOM.style.width = `calc(100% - ${ window.pageYOffset > 0 ? shaveWidth : 0 }px)`;
          },
          pullUp: () => {
            const trigger = {
              start: 235,
              stop: 435,
            };

            const yValue= this.getPerc(mastheadDOMbottomSpace, trigger.start, trigger.stop);
            if ( !mastheadDOMbottomSpace > 235 ) { return; }
            mastheadDOM.style.transform = `translateY(-${ yValue}px)`;
          },
        };
        primaryContainerAnim.shrink();
        primaryContainerAnim.pullUp();

        // Carousel transition
        const secondaryContainerAnim = {
          //  `trigger` reference mastheadDOM.getBoundingClientRect().bottom
          fadeIn: () => {
            const trigger = {
              start: 190,
              stop: 235,
            };
            const opacity = this.getPerc(mastheadDOMbottomSpace, trigger.start, trigger.stop) / 100;

            carouselDOM.style.opacity = opacity;
          },
          stretch: () => {
            const trigger = {
              start: 440,
              stop: 665,
            };

            const perc = this.getPerc(mastheadDOMbottomSpace, trigger.start, trigger.stop) / 100;
            const addWidth = (window.innerWidth - window.innerWidth * 0.57) * perc;
            carouselDOM.style.width = `calc((57% + ${ addWidth > 0 ? addWidth : 0 }px)`;
          },
        };
        secondaryContainerAnim.fadeIn();
        secondaryContainerAnim.stretch();
      }

      onScroll(scrollEvent => {
        // if mobile, don't run the animation
        if (breakpoint() === 'mobile') { return; }
        // for some reason, the throttleRAF function executes the callback more than needed, causing lags even after the animation ended
        // that also caused some computations left hanging causing the browser to freeze (not always, but has chances)
        debounce(requestAnimationFrame(() => resizeAnimation(scrollEvent.scrollY)));
      });
    },

  /**
   * 3. RENDERING
   *
   * Use `finalize` for actions that should run
   * as soon as the module is rendered to the page
   * - e.g. Event Listeners, DOM Manipulations,
   *        MessageBus Publishers, etc
   */
    finalize(GlobalEvents, MessageBus) {
        const { onScroll, onResize, debounced } = GlobalEvents;
        this.startHero(GlobalEvents, MessageBus);
    },

    // local util
    getPerc(input, min, max) {
      const val = ((input - min) * 100) / (max - min);
      return parseFloat(val).toFixed(2);
    },
};



// WEBPACK FOOTER //
// ../views/modules/homepage-hero/homepage-hero.js